import React, { useState, useEffect } from 'react';
import api from '../../services/api';

interface PolicyConfig {
  version: string;
  updated_at: string;
  selector_top50: any; // Legacy API field name for Trading Universe config
  portfolio_limits: any;
  orders_execution: any;
  signals_confidence: any;
  cadence: any;
  pretrade_filters: any;
  exits: any;
  providers_consensus: any;
  biweekly_review?: any;
  earnings_event_controls?: any;
  notifications?: any;
  logging?: any;
  observability?: any;
  pdt_gate?: any;
  enforcement_order?: any;
  ai_learning?: any;
  risk_management?: any;
  kpi_tracking?: any;
  performance_targeting?: any;
  portfolio_vol_targeting?: any;
  sizing_policy?: any;
  alpha_buckets?: any;
  split_handling?: any;
}

interface PolicyResponse {
  success: boolean;
  data: PolicyConfig;
  meta: {
    version: string;
    policyVersion: string;
    lastLoaded: string;
  };
}

interface SummaryItem {
  label: string;
  value?: string | number | React.ReactNode | null;
}

const formatNumber = (value?: number | string | null, options: Intl.NumberFormatOptions = {}) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return num.toLocaleString(undefined, options);
};

const formatPercent = (value?: number | string | null, decimals = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return `${(num * 100).toFixed(decimals)}%`;
};

const formatCurrency = (value?: number | string | null, options: Intl.NumberFormatOptions = {}) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return `$${num.toLocaleString(undefined, { maximumFractionDigits: 0, ...options })}`;
};

const formatMinutes = (value?: number | string | null) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return `${num} min`;
};

const formatList = (values?: string[] | null) => {
  if (!values || values.length === 0) return null;
  return values.join(', ');
};

const toTitle = (text: string) =>
  text
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const SummaryGrid: React.FC<{ items: SummaryItem[] }> = ({ items }) => {
  const filtered = items.filter((item) => item.value !== undefined && item.value !== null && item.value !== '');
  if (!filtered.length) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {filtered.map((item) => (
        <div key={item.label} className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-3">
          <p className="text-xs uppercase tracking-wide text-neutral-400">{item.label}</p>
          <div className="text-sm font-semibold text-white mt-1">{item.value}</div>
        </div>
      ))}
    </div>
  );
};

const BadgeGroup: React.FC<{ label: string; values?: string[] | null }> = ({ label, values }) => {
  if (!values || !values.length) return null;
  return (
    <div className="space-y-1">
      <p className="text-sm text-neutral-400">{label}</p>
      <div className="flex flex-wrap gap-2">
        {values.map((value) => (
          <span key={value} className="px-2 py-0.5 rounded-full bg-neutral-800 text-neutral-100 text-xs border border-neutral-700">
            {value}
          </span>
        ))}
      </div>
    </div>
  );
};

const EmptySummary: React.FC<{ data?: any }> = ({ data }) => {
  if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
    return (
      <p className="text-sm text-neutral-400 italic">
        This section is not configured in the current policy.
      </p>
    );
  }
  return (
    <p className="text-sm text-neutral-400">
      No summary information available for this section. The raw JSON is available by clicking "View Raw JSON".
    </p>
  );
};

const renderTop50Summary = (data: any) => {
  if (!data) return <EmptySummary />;
  const eligibility = data.eligibility || {};
  const thresholds = eligibility.regime_aware_thresholds;
  const thresholdSummary =
    thresholds &&
    Object.entries(thresholds)
      .map(([regime, values]) => {
        const mc = formatCurrency((values as any).market_cap_min_usd);
        const adv = formatCurrency((values as any).dollar_adv20_min);
        return `${toTitle(regime)} ≥ ${mc || '—'} cap / ${adv || '—'} ADV`;
      })
      .join(' · ');

  return (
    <div className="space-y-4">
      {data.purpose && <p className="text-sm text-neutral-300">{data.purpose}</p>}
      <SummaryGrid
        items={[
          { label: 'Schedule', value: `${formatMinutes(data.interval_minutes) || '—'} • ${formatList(data.market_hours_et) || 'market hours'}` },
          { label: 'Minimum Price', value: formatCurrency(eligibility.price_min) },
          { label: 'Max Spread', value: formatPercent(eligibility.spread_max_pct) },
          { label: 'Free Float Minimum', value: formatNumber(eligibility.free_float_shares_min) },
          { label: 'Min Market Cap', value: formatCurrency(eligibility.market_cap_min_usd) },
          { label: 'Min Dollar ADV (20d)', value: formatCurrency(eligibility.dollar_adv20_min) },
          { label: 'Recent IPO Policy', value: `${eligibility.recent_ipo_policy || '—'} (${eligibility.recent_ipo_days || 0} days)` },
          { label: 'Earnings Policy', value: eligibility.earnings_policy ? toTitle(eligibility.earnings_policy) : null },
          { label: 'Regime Thresholds', value: thresholdSummary }
        ]}
      />
      <BadgeGroup label="Allowed Exchanges" values={eligibility.allow_exchanges} />
      <BadgeGroup label="Excluded Types" values={eligibility.exclude_types} />
      {data.sectors && (
        <SummaryGrid
          items={[
            { label: 'Sector Min / Max', value: `${data.sectors.min_per_sector || 0} / ${data.sectors.max_per_sector || 0}` },
            {
              label: 'Regime Sector Caps',
              value:
                (() => {
                  const regimeCaps = data.sectors.regime_aware_caps as Record<string, { max_per_sector?: number }> | undefined;
                  if (!regimeCaps) return null;
                  return Object.entries(regimeCaps)
                    .map(([regime, vals]) => `${toTitle(regime)}: ${vals?.max_per_sector ?? '—'}`)
                    .join(', ');
                })()
            }
          ]}
        />
      )}
    </div>
  );
};

const renderPortfolioSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  const warmup = data.new_account_warmup || {};
  const profitLock = data.daily_profit_lock || {};
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Max Open Positions', value: data.max_open_positions },
          { label: 'Max Position % per Symbol', value: formatPercent(data.max_position_pct_per_symbol) },
          { label: 'Sector Exposure Cap', value: formatPercent(data.sector_exposure_cap) },
          { label: 'Daily Max Loss', value: formatPercent(data.daily_max_loss_pct) },
          { label: 'Min Cash Buffer', value: formatPercent(data.min_cash_buffer_pct) },
          {
            label: 'Regime Position Caps',
            value: (() => {
              const caps = data.position_caps_by_regime as Record<string, { max_position_pct_per_symbol?: number }> | undefined;
              if (!caps) return null;
              return Object.entries(caps)
                .map(([regime, vals]) => `${toTitle(regime)}: ${formatPercent(vals?.max_position_pct_per_symbol)}`)
                .join(', ');
            })()
          },
        ]}
      />
      {warmup.enabled && (
        <SummaryGrid
          items={[
            { label: 'New Account Warmup', value: `${warmup.trading_days} trading days at ${formatPercent(warmup.base_position_pct_multiplier, 0)} size` },
            { label: 'Warmup Position Cap', value: warmup.max_open_positions }
          ]}
        />
      )}
      {profitLock.enabled && (
        <SummaryGrid
          items={[
            { label: 'Daily Profit Lock', value: `${formatPercent(profitLock.threshold_pct)} → ${profitLock.actions?.join(', ')}` }
          ]}
        />
      )}
    </div>
  );
};

const renderOrdersSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Default Buy Order', value: `${data.buy?.default || '—'} (anchor ${data.buy?.anchor || '—'})` },
          {
            label: 'Buy Limit Band',
            value: data.buy?.limit_band_pct ? `${(data.buy.limit_band_pct[0] * 100).toFixed(1)}–${(data.buy.limit_band_pct[1] * 100).toFixed(1)} bps` : null
          },
          { label: 'Market Allowed', value: data.buy?.market_allowed_on_top_liquidity ? 'Yes on top liquidity' : 'Prefers limit' },
          { label: 'Sell Default', value: data.sell?.default ? toTitle(data.sell.default) : '—' },
          { label: 'Spread Guard', value: data.spread_guard_bps ? `${data.spread_guard_bps.market_allowed_threshold} bps` : null },
          { label: 'Partial Fill Policy', value: data.partial_fill_policy ? `${data.partial_fill_policy.timebox_sec}s, ${data.partial_fill_policy.reprice_attempts} reprice` : null },
          { label: 'Anti-Whipsaw', value: data.anti_whipsaw ? `${data.anti_whipsaw.bar_minutes}-min bars, same-bar flip forbidden` : null }
        ]}
      />
      {data.idempotency?.enabled && (
        <p className="text-sm text-neutral-300">Idempotency keys required (`{data.idempotency.key_header}`) with {data.idempotency.ttl_seconds}s TTL.</p>
      )}
    </div>
  );
};

const renderSignalsSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Open New Positions On', value: formatList(data.open_new_positions_on) },
          { label: 'Base Position %', value: formatPercent(data.base_position_pct) },
          {
            label: 'Confidence Weights',
            value:
              data.confidence_weights &&
              Object.entries(data.confidence_weights)
                .map(([level, weight]) => `${level}: ×${weight}`)
                .join(' · ')
          },
          { label: 'Low Confidence Rule', value: data.low_conf_only_trim_or_exit ? 'Only trim or exit' : 'Allow adds' },
          { label: 'Volatility Guard', value: data.volatility_guard ? `Compare to ${data.volatility_guard.compare_to}` : null },
          { label: 'Scaling Add Rules', value: data.scaling_add_rules ? `≥ ${data.scaling_add_rules.min_open_pnl_R}R, cap ${formatPercent(data.scaling_add_rules.never_exceed_pct_per_symbol)}` : null }
        ]}
      />
      {data.campaign_mode?.enabled && (
        <SummaryGrid
          items={[
            { label: 'Campaign Mode', value: data.campaign_mode.name },
            {
              label: 'Risk per Trade',
              value: `Baseline ${formatPercent(data.campaign_mode.risk_per_trade_pct?.baseline)} / Campaign ${formatPercent(data.campaign_mode.risk_per_trade_pct?.campaign)}`
            },
            {
              label: 'Activation',
              value: `Regimes: ${formatList(data.campaign_mode.activation?.when_regime_in) || '—'}`
            }
          ]}
        />
      )}
    </div>
  );
};

const renderCadenceSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  const screening = data.screening || {};
  const entryBudgets = data.entry_budgets || {};
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Timezone', value: data.timezone },
          { label: 'Screening Interval', value: screening.enabled ? `${formatMinutes(screening.interval_minutes)} (${formatList(screening.market_hours_et)})` : 'Disabled' },
          { label: 'Reconciliation', value: data.reconciliation?.enabled ? `${formatMinutes(data.reconciliation.interval_minutes)} interval` : 'Disabled' },
          { label: 'Weekly Rebalance', value: data.weekly_rebalance?.enabled ? data.weekly_rebalance.time_et : 'Disabled' }
        ]}
      />
      {Object.keys(entryBudgets).length > 0 && (
        <SummaryGrid
          items={Object.entries(entryBudgets).map(([regime, config]) => ({
            label: `${toTitle(regime)} Entries`,
            value: `${(config as any).max_new_entries_per_day} / day${(config as any).signals_required ? `, signals: ${formatList((config as any).signals_required)}` : ''}`
          }))}
        />
      )}
    </div>
  );
};

const renderPretradeSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Min Price', value: formatCurrency(data.min_price_usd) },
          { label: 'Min Market Cap', value: formatCurrency(data.min_market_cap_usd) },
          { label: 'Max Median Spread', value: data.max_median_spread_bps ? `${data.max_median_spread_bps} bps` : null },
          { label: 'Live Spread Hard Cap', value: data.live_liquidity_guards ? formatPercent(data.live_liquidity_guards.spread_pct_hard_cap) : null },
          { label: 'Restricted List', value: data.restricted_list_enabled ? 'Enabled' : 'Disabled' }
        ]}
      />
      <BadgeGroup label="Skip States" values={data.skip_states} />
      {data.live_liquidity_guards?.notes && <p className="text-sm text-neutral-300">{data.live_liquidity_guards.notes}</p>}
    </div>
  );
};

const renderExitsSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          {
            label: 'Trailing Stops',
            value:
              data.trailing_stops &&
              ['HIGH', 'MED', 'LOW']
                .filter((level) => data.trailing_stops[level])
                .map((level) => `${level}: ${formatPercent(data.trailing_stops[level].trail_pct)} / ×${data.trailing_stops[level].atr_multiple}`)
                .join(' · ')
          },
          { label: 'Hard Stop Guard', value: data.hard_stop_gap_guard_multiple_of_trail ? `${data.hard_stop_gap_guard_multiple_of_trail}× trail` : null },
          {
            label: 'Profit Tiers',
            value: data.profit_tiers
              ?.map((tier: any) => `${tier.trigger_R}R → take ${formatPercent(tier.take_pct)} (${tier.move_trail_to || tier.tighten_trail_pct || ''})`)
              .join(' · ')
          },
          {
            label: 'Time Stops',
            value: data.time_stops ? `First check ${data.time_stops.first_check_days}d, exit by ${data.time_stops.final_exit_days}d` : null
          },
          { label: 'Earnings Policy', value: data.earnings_day_flat_by_prior_close_unless_override ? 'Flat by prior close unless override' : 'Allow holds' }
        ]}
      />
    </div>
  );
};

const renderProvidersSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  const keysToShow = ['quotes_ohlc', 'daily_eod', 'news_sentiment', 'earnings', 'fundamentals', 'macro'];
  const feeds = keysToShow
    .filter((key) => data[key])
    .map((key) => ({
      label: toTitle(key),
      value: `${formatList(data[key].primaries) || '—'}${data[key].fallbacks?.length ? ` (fallbacks: ${formatList(data[key].fallbacks)})` : ''}`
    }));

  return (
    <div className="space-y-4">
      <SummaryGrid items={feeds} />
      {data.consensus_policy && (
        <SummaryGrid
          items={[
            {
              label: 'Consensus Policy',
              value: `Tolerance ${data.consensus_policy.quote_price_tolerance_bps} bps • Freshness ${data.consensus_policy.min_freshness_sec}s`
            },
            {
              label: 'Action Gating',
              value: data.consensus_policy.action_gating?.open
                ? `Open: ${data.consensus_policy.action_gating.open}, Add: ${data.consensus_policy.action_gating.add}`
                : null
            }
          ]}
        />
      )}
    </div>
  );
};

const renderBiweeklySummary = (data: any) => {
  if (!data) return <EmptySummary />;
  const exposure = data.exposure_and_guardrails || {};
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Cadence', value: data.enabled ? `Every ${data.cadence_days || 14} days (${data.schedule || '—'})` : 'Disabled' },
          { label: 'Execution Time', value: data.execution_time_et },
          { label: 'Allowed Actions', value: ['allow_adds', 'allow_trims', 'allow_exits', 'allow_refills'].filter((key) => data[key]).map(toTitle).join(', ') || '—' },
          { label: 'Turnover Cap', value: data.turnover_cap_pct_per_period ? formatPercent(data.turnover_cap_pct_per_period) : null },
          { label: 'Earnings Blackout', value: data.earnings_blackout_days ? `${data.earnings_blackout_days} days` : null },
          {
            label: 'Regime Caps',
            value:
              exposure.regime_caps &&
              Object.entries(exposure.regime_caps)
                .map(([regime, cap]) => `${toTitle(regime)}: ${formatPercent((cap as any).max_gross_pct)}`)
                .join(' · ')
          },
          { label: 'Min Cash Buffer', value: formatPercent(exposure.min_cash_buffer_pct) },
          { label: 'Max Intraday Drawdown', value: formatPercent(exposure.max_intraday_drawdown_pct) }
        ]}
      />
      {data.position_limits && (
        <SummaryGrid
          items={[
            { label: 'Single Name Cap', value: formatPercent(data.position_limits.single_name_max_pct) },
            { label: 'Sector Cap', value: formatPercent(data.position_limits.sector_max_pct) },
            { label: 'Max Concurrent Positions', value: data.position_limits.max_concurrent_positions },
            { label: 'Overlap Blocking', value: data.position_limits.overlap_blocking ? 'Enabled' : 'Disabled' }
          ]}
        />
      )}
    </div>
  );
};
const renderBiweeklyEntryFilters = (data: any) => {
  if (!data) return <EmptySummary />;
  const technicalChecks = [
    { key: 'require_50dma_gt_200dma', label: '50DMA > 200DMA' },
    { key: 'require_rs_gt_0', label: 'RS > 0' },
    { key: 'require_sector_rs_pos', label: 'Sector RS positive' },
    { key: 'require_name_rs_pos', label: 'Name RS positive' }
  ]
    .filter((item) => data[item.key])
    .map((item) => item.label);

  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Technical Filters', value: technicalChecks.length ? technicalChecks.join(', ') : '—' },
          { label: 'Min ADV (USD)', value: formatCurrency(data.min_adv20_usd) },
          { label: 'Max Spread', value: data.max_spread_bps ? `${data.max_spread_bps} bps` : null },
          { label: 'Earnings Blackout', value: data.earnings_blackout_days ? `${data.earnings_blackout_days} days` : null },
          { label: 'Correlation Block', value: data.block_same_industry_if_corr_gt ? `Block > ${data.block_same_industry_if_corr_gt}` : null }
        ]}
      />
    </div>
  );
};

const renderEarningsControlsSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  const window = data.block_new_entries_window || {};
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Block New Entries', value: `From ${window.from_trading_days ?? '—'} to ${window.to_trading_days ?? '—'} trading days around earnings` },
          { label: 'Allow Override', value: data.allow_override_if_confidence ? `Only if confidence = ${data.allow_override_if_confidence}` : 'No' },
          { label: 'Default Action', value: data.default_reduce_or_exit ? 'Reduce/exit by default' : 'Hold allowed' },
          { label: 'News Shock', value: data.news_shock_allows_urgent_sell ? 'Urgent sell allowed' : 'No special handling' }
        ]}
      />
    </div>
  );
};

const renderNotificationsSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  return (
    <div className="space-y-4">
      <SummaryGrid items={[{ label: 'Channel', value: toTitle(data.channel || '—') }]} />
      <BadgeGroup label="Events" values={data.events} />
    </div>
  );
};

const renderLoggingSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  const boolToText = (val?: boolean) => (val ? 'Yes' : 'No');
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Log AI Prompts', value: boolToText(data.ai_prompts_and_responses) },
          { label: 'Log API Usage', value: boolToText(data.log_api_used) },
          { label: 'Log Decision Reasons', value: boolToText(data.log_decision_reason) },
          { label: 'User Visible Logs', value: data.user_visible_logs ? Object.entries(data.user_visible_logs).filter(([, enabled]) => enabled).map(([key]) => toTitle(key)).join(', ') : null }
        ]}
      />
    </div>
  );
};

const renderObservabilitySummary = (data: any) => {
  if (!data) return <EmptySummary />;
  const metrics = data.metrics ? Object.entries(data.metrics).filter(([, enabled]) => enabled).map(([key]) => toTitle(key)).slice(0, 6) : [];
  const alerts = data.alerts || {};
  return (
    <div className="space-y-4">
      <BadgeGroup label="Tracked Metrics" values={metrics} />
      <SummaryGrid
        items={[
          { label: 'Rate Limit Threshold', value: alerts.r429_threshold ? `${formatPercent(alerts.r429_threshold)} in ${alerts.r429_window_minutes}m` : null },
          { label: 'AI Degradation Alert', value: alerts.ai_degraded_threshold ? `${formatPercent(alerts.ai_degraded_threshold)} over ${alerts.ai_degraded_window_minutes}m` : null },
          { label: 'Auto switch to fallback first', value: alerts.switch_to_fallback_first ? 'Yes' : 'No' },
          { label: 'Notify On-Call', value: alerts.notify_oncall ? 'Yes' : 'No' }
        ]}
      />
    </div>
  );
};

const renderPdtSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Status', value: data.enabled ? 'Enabled' : 'Disabled' },
          { label: 'Min Equity', value: formatCurrency(data.min_equity_usd_for_day_trading) },
          { label: 'Day Trade Limit (rolling 5d)', value: data.pdt_threshold_day_trades },
          { label: 'Max Trades if Not Qualified', value: data.max_day_trades_if_not_qualified },
          { label: 'DTBP Multiple', value: data.dtbp_rule ? `${data.dtbp_rule.multiple}×` : null },
          { label: 'Time Window', value: formatList(data.time_window_et) },
          { label: 'On Violation', value: data.on_violation?.message }
        ]}
      />
    </div>
  );
};

const renderEnforcementSummary = (data: any) => {
  if (!data || !Array.isArray(data)) return <EmptySummary />;
  return (
    <div className="space-y-2">
      <p className="text-sm text-neutral-300">Requests must pass each gate in order:</p>
      <ol className="list-decimal list-inside text-sm text-neutral-200 space-y-1">
        {data.map((step: string) => (
          <li key={step}>{toTitle(step.replace(/_/g, ' '))}</li>
        ))}
      </ol>
    </div>
  );
};

const renderAiLearningSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Mode', value: data.mode ? toTitle(data.mode) : '—' },
          { label: 'Canary Allocation', value: formatPercent(data.rollout?.canary_allocation) },
          { label: 'Promote When', value: data.rollout?.promote_when ? `p95 uplift > 0 & drawdown not worse for ${data.rollout.promote_when.min_duration_days}d` : null },
          { label: 'Rollback When', value: data.rollout?.rollback_when ? `${data.rollout.rollback_when.underperform_weeks} weeks underperform or PSI > ${data.rollout.rollback_when.psi_drift_gt}` : null },
          { label: 'Training Cadence', value: data.training ? `${toTitle(data.training.cadence || '—')} @ ${data.training.time_et}` : null },
          { label: 'Inference Blend', value: data.inference?.combine_with_llm ? `${data.inference.combine_with_llm} (ML ${data.inference.weights?.ml ?? 0}, LLM ${data.inference.weights?.llm ?? 0})` : null },
          { label: 'Exploration Strategy', value: data.exploration?.enabled ? `${toTitle(data.exploration.strategy)} at ${formatPercent(data.exploration.epsilon_pct)} epsilon` : 'Disabled' }
        ]}
      />
    </div>
  );
};

const renderRiskManagementSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  const order = Array.isArray(data.stop_order_of_operations)
    ? data.stop_order_of_operations.map((step: string) => toTitle(step.replace(/_/g, ' '))).join(' → ')
    : null;
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Risk per Trade', value: formatPercent(data.risk_per_trade_pct) },
          { label: 'Hard Stop', value: formatPercent(data.hard_stop_pct) },
          { label: 'Time Stop', value: data.time_stop_days ? `${data.time_stop_days} days` : null },
          { label: 'Move to Breakeven', value: formatPercent(data.move_to_breakeven_pct) },
          { label: 'Partial Take Profit', value: `${formatPercent(data.partial_take_profit_pct)} • ${formatPercent(data.partial_take_fraction)} of size` },
          { label: 'Trail Activation', value: `${formatPercent(data.trail_activation_profit_pct)} trigger • ${formatPercent(data.trail_distance_pct)} trail` },
          { label: 'Stop Order of Operations', value: order }
        ]}
      />
    </div>
  );
};

const renderKpiSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  const metricsSource = data.kpi_metrics as Record<string, { target?: number; target_max?: number; min_acceptable?: number }> | undefined;
  const metrics =
    metricsSource &&
    Object.entries(metricsSource).map(([metric, metricValues]) => {
      if (metricValues.target !== undefined) {
        return `${toTitle(metric)} target ${metricValues.target}`;
      }
      if (metricValues.target_max !== undefined) {
        return `${toTitle(metric)} ≤ ${metricValues.target_max}`;
      }
      if (metricValues.min_acceptable !== undefined) {
        return `${toTitle(metric)} min ${metricValues.min_acceptable}`;
      }
      return null;
    });
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Status', value: data.enabled ? 'Tracking enabled' : 'Disabled' },
          { label: 'Reporting Window', value: `${data.reporting_window_days || 0} days` },
          { label: 'Metrics Tracked', value: metrics?.filter(Boolean).join(' · ') || '—' }
        ]}
      />
    </div>
  );
};

const renderPerformanceTargetingSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  const controls = data.controls || {};
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Status', value: data.enabled ? 'Enabled' : 'Disabled' },
          { label: 'Annual Return Target', value: formatPercent(data.annual_return_target_pct / 100) },
          { label: 'Daily Target', value: formatPercent(data.target_daily_return, 2) },
          {
            label: 'Control Bands',
            value: (() => {
              const bands = data.bands as Record<string, number> | undefined;
              if (!bands) return null;
              return Object.entries(bands)
                .map(([band, val]) => `${toTitle(band)}: ${formatPercent(val)}`)
                .join(' · ');
            })()
          },
          {
            label: 'Risk Per Trade Adjustment',
            value: (() => {
              const multipliers = controls.risk_per_trade_pct_multiplier as Record<string, number> | undefined;
              if (!multipliers) return null;
              return Object.entries(multipliers)
                .map(([band, mul]) => `${toTitle(band)}: ×${mul}`)
                .join(' · ');
            })()
          },
          {
            label: 'New Entries Adjustment',
            value: (() => {
              const deltas = controls.max_new_entries_per_day_delta as Record<string, number> | undefined;
              if (!deltas) return null;
              return Object.entries(deltas)
                .map(([band, delta]) => `${toTitle(band)}: ${delta >= 0 ? '+' : ''}${delta}`)
                .join(' · ');
            })()
          },
        ]}
      />
      {data.constraints && (
        <SummaryGrid
          items={[
            { label: 'Kelly Cap', value: data.constraints.kelly_fraction_cap ? `${formatPercent(data.constraints.kelly_fraction_cap)} of Kelly` : null },
            { label: 'Never Exceed', value: 'Tied to portfolio limits (position %, max positions, daily loss)' }
          ]}
        />
      )}
    </div>
  );
};

const renderPortfolioVolSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Status', value: data.enabled ? 'Enabled' : 'Disabled' },
          { label: 'Target Daily Volatility', value: data.target_daily_vol_pct ? `${data.target_daily_vol_pct}%` : null },
          { label: 'Band', value: data.band_pct ? `${formatPercent(data.band_pct)} band` : null },
          { label: 'Lookback Window', value: `${data.lookback_days || 0} days` },
          {
            label: 'Adjustments',
            value: data.adjust
              ? `Above band → ×${data.adjust.if_above_band_reduce_position_multiplier}, Below band → ×${data.adjust.if_below_band_increase_position_multiplier}`
              : null
          }
        ]}
      />
      {data.applies_to && <BadgeGroup label="Applies To" values={data.applies_to} />}
    </div>
  );
};

const renderSizingPolicySummary = (data: any) => {
  if (!data) return <EmptySummary />;
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Status', value: data.enabled ? toTitle(data.method || 'Enabled') : 'Disabled' },
          { label: 'Inputs', value: data.inputs?.map((input: string) => input.replace(/_/g, ' ')).join(', ') },
          {
            label: 'Position Map',
            value:
              data.map_to_position_pct &&
              Object.entries(data.map_to_position_pct)
                .map(([level, pct]) => `${level}: ×${pct}`)
                .join(' · ')
          },
          { label: 'Kelly Cap', value: data.kelly_fraction_cap ? `${formatPercent(data.kelly_fraction_cap)} of Kelly` : null },
          { label: 'Hard Caps', value: data.hard_caps?.never_exceed_pct_per_symbol ? 'Linked to portfolio limits' : null }
        ]}
      />
    </div>
  );
};

const renderAlphaBucketsSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  const budgets = data.budget_by_regime
    ? Object.entries(data.budget_by_regime)
        .map(([regime, allocations]) => `${toTitle(regime)} → ${Object.entries(allocations as Record<string, number>).map(([bucket, pct]) => `${bucket}: ${formatPercent(pct)}`).join(', ')}`)
        .join(' | ')
    : null;
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Status', value: data.enabled ? 'Enabled' : 'Disabled' },
          { label: 'Buckets', value: formatList(data.buckets) },
          { label: 'Budget by Regime', value: budgets },
          { label: 'Enforcement', value: data.enforcement }
        ]}
      />
    </div>
  );
};

const renderSplitHandlingSummary = (data: any) => {
  if (!data) return <EmptySummary />;
  return (
    <div className="space-y-4">
      <SummaryGrid
        items={[
          { label: 'Status', value: data.enabled ? 'Enabled' : 'Disabled' },
          { label: 'Auto Adjust Historical Data', value: data.auto_adjust_historical_ohlc ? 'Yes' : 'No' },
          { label: 'Reseed Stops Post Split', value: data.reseed_stops_post_split ? 'Yes' : 'No' }
        ]}
      />
      <BadgeGroup label="Detection Methods" values={data.detection_methods} />
    </div>
  );
};

const summaryRenderers: Record<string, (data: any) => React.ReactNode> = {
  selector_top50: renderTop50Summary,
  portfolio_limits: renderPortfolioSummary,
  orders_execution: renderOrdersSummary,
  signals_confidence: renderSignalsSummary,
  cadence: renderCadenceSummary,
  pretrade_filters: renderPretradeSummary,
  exits: renderExitsSummary,
  providers_consensus: renderProvidersSummary,
  biweekly_review: renderBiweeklySummary,
  biweekly_entry_filters: renderBiweeklyEntryFilters,
  earnings_event_controls: renderEarningsControlsSummary,
  notifications: renderNotificationsSummary,
  logging: renderLoggingSummary,
  observability: renderObservabilitySummary,
  pdt_gate: renderPdtSummary,
  enforcement_order: renderEnforcementSummary,
  ai_learning: renderAiLearningSummary,
  risk_management: renderRiskManagementSummary,
  kpi_tracking: renderKpiSummary,
  performance_targeting: renderPerformanceTargetingSummary,
  portfolio_vol_targeting: renderPortfolioVolSummary,
  sizing_policy: renderSizingPolicySummary,
  alpha_buckets: renderAlphaBucketsSummary,
  split_handling: renderSplitHandlingSummary
};

/**
 * Policy Configuration Dashboard Component
 * Displays current policy configuration for admin users
 */
const PolicyConfigDashboard: React.FC = () => {
  const [policy, setPolicy] = useState<PolicyConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['overview']));
  const [rawSections, setRawSections] = useState<Set<string>>(new Set());

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/admin/policy');
      const data: PolicyResponse = response.data;
      
      if (data.success) {
        setPolicy(data.data);
      } else {
        setError('Failed to load policy configuration');
      }
    } catch (err: any) {
      console.error('Policy fetch error:', err);
      setError(err?.response?.data?.error?.message || 'Failed to load policy configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicy();
  }, []);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const toggleRawSection = (section: string) => {
    const next = new Set(rawSections);
    if (next.has(section)) {
      next.delete(section);
    } else {
      next.add(section);
    }
    setRawSections(next);
  };

  const renderConfigSection = (title: string, key: string, data: any) => {
    const isExpanded = expandedSections.has(key);
    const showRaw = rawSections.has(key);
    const summary = summaryRenderers[key]?.(data);
    
    return (
      <div key={key} className="bg-neutral-900 rounded-lg p-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <button
            onClick={() => toggleSection(key)}
            className="flex items-center justify-between w-full text-left"
          >
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <span className={`text-neutral-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </button>
          <button
            onClick={(event) => {
              event.stopPropagation();
              toggleRawSection(key);
            }}
            className="self-start sm:self-auto text-xs px-3 py-1 rounded border border-neutral-700 text-neutral-300 hover:bg-neutral-800 transition-colors"
          >
            {showRaw ? 'Hide Raw JSON' : 'View Raw JSON'}
          </button>
        </div>
        
        {isExpanded && (
          <div className="mt-4 space-y-4">
            {summary || <EmptySummary data={data} />}
            {showRaw && (
              <pre className="bg-neutral-800 p-4 rounded text-sm text-neutral-300 overflow-x-auto">
                {JSON.stringify(data, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-neutral-400">Loading policy configuration...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-6">
        <h3 className="text-red-400 font-semibold mb-2">Error Loading Policy</h3>
        <p className="text-red-300">{error}</p>
        <button
          onClick={fetchPolicy}
          className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="text-neutral-400 text-center py-8">
        No policy configuration available
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview */}
      <div className="bg-neutral-900 rounded-lg p-6">
        <h2 className="text-xl font-bold text-white mb-4">Policy Configuration Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-neutral-400">Version</p>
            <p className="text-lg font-semibold text-cyan-400">{policy.version}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-400">Last Updated</p>
            <p className="text-lg font-semibold text-green-400">
              {new Date(policy.updated_at).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-neutral-400">Status</p>
            <p className="text-lg font-semibold text-green-400">Active</p>
          </div>
        </div>
      </div>

      {/* Configuration Sections */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-white">Configuration Sections</h2>
        
        {/* Only render sections that exist and have data */}
        {policy.selector_top50 && renderConfigSection('Trading Universe', 'selector_top50', policy.selector_top50)}
        {policy.portfolio_limits && renderConfigSection('Portfolio Limits', 'portfolio_limits', policy.portfolio_limits)}
        {policy.orders_execution && renderConfigSection('Orders Execution', 'orders_execution', policy.orders_execution)}
        {policy.signals_confidence && renderConfigSection('Signals Confidence', 'signals_confidence', policy.signals_confidence)}
        {policy.cadence && renderConfigSection('Cadence', 'cadence', policy.cadence)}
        {policy.pretrade_filters && renderConfigSection('Pre-trade Filters', 'pretrade_filters', policy.pretrade_filters)}
        {policy.exits && renderConfigSection('Exits', 'exits', policy.exits)}
        {policy.providers_consensus && renderConfigSection('Providers Consensus', 'providers_consensus', policy.providers_consensus)}
        {policy.biweekly_review && renderConfigSection('Biweekly Review', 'biweekly_review', policy.biweekly_review)}
        {policy.biweekly_review?.entry_filters && renderConfigSection('Biweekly Entry Filters', 'biweekly_entry_filters', policy.biweekly_review.entry_filters)}
        {policy.earnings_event_controls && renderConfigSection('Earnings Event Controls', 'earnings_event_controls', policy.earnings_event_controls)}
        {policy.notifications && renderConfigSection('Notifications', 'notifications', policy.notifications)}
        {policy.logging && renderConfigSection('Logging', 'logging', policy.logging)}
        {policy.observability && renderConfigSection('Observability', 'observability', policy.observability)}
        {policy.pdt_gate && renderConfigSection('PDT Gate', 'pdt_gate', policy.pdt_gate)}
        {policy.enforcement_order && renderConfigSection('Enforcement Order', 'enforcement_order', policy.enforcement_order)}
        {policy.ai_learning && renderConfigSection('AI Learning', 'ai_learning', policy.ai_learning)}
        {policy.risk_management && renderConfigSection('Risk Management', 'risk_management', policy.risk_management)}
        {policy.kpi_tracking && renderConfigSection('KPI Tracking', 'kpi_tracking', policy.kpi_tracking)}
        {policy.performance_targeting && renderConfigSection('Performance Targeting', 'performance_targeting', policy.performance_targeting)}
        {policy.portfolio_vol_targeting && renderConfigSection('Portfolio Volatility Targeting', 'portfolio_vol_targeting', policy.portfolio_vol_targeting)}
        {policy.sizing_policy && renderConfigSection('Sizing Policy', 'sizing_policy', policy.sizing_policy)}
        {policy.alpha_buckets && renderConfigSection('Alpha Buckets', 'alpha_buckets', policy.alpha_buckets)}
        {policy.split_handling && renderConfigSection('Split Handling', 'split_handling', policy.split_handling)}
      </div>

      {/* Actions */}
      <div className="bg-neutral-900 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Actions</h3>
        <div className="flex space-x-4">
          <button
            onClick={fetchPolicy}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
          >
            Refresh Policy
          </button>
          <button
            onClick={() => {
              const allExpanded = new Set([
                'overview', 'selector_top50', 'portfolio_limits', 'orders_execution',
                'signals_confidence', 'cadence', 'pretrade_filters', 'exits', 'providers_consensus'
              ]);
              setExpandedSections(allExpanded);
            }}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={() => setExpandedSections(new Set(['overview']))}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
          >
            Collapse All
          </button>
        </div>
      </div>
    </div>
  );
};

export default PolicyConfigDashboard;
